import type { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import Link from "next/link";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
} from "lucide-react";

import SeoCtaLink from "@/components/SeoCtaLink";
import {
  alternativePages,
  getAlternativePage,
  getAlternativeRecommendations,
  getCompetitorProfile,
  type AlternativePage,
  type CompetitorProfile,
  type ProductSummary,
} from "@/src/seo/competitors";
import { FREE_TRIAL_APPLICATION_CREDITS } from "@src/config/freeTrial";
import Footer from "pagecomponents/newcomponents/Footer";
import Navbar from "pagecomponents/newcomponents/Navbar";

import styles from "../compare/style.module.css";

type AlternativesPageProps = {
  page: AlternativePage;
  competitor: CompetitorProfile;
  recommendations: ProductSummary[];
  relatedCompetitors: CompetitorProfile[];
};

const siteUrl = "https://www.lifeshack.com";

const workflowLabels: Record<ProductSummary["workflow"], string> = {
  browser_extension: "Browser extension",
  cloud_agent: "Cloud application agent",
  managed_service: "Managed application service",
  web_workspace: "Web workspace",
};

function signupHref(slug: string) {
  return `/signup/?utm_source=seo&utm_medium=organic&utm_campaign=alternatives&utm_content=${slug}`;
}

export default function AlternativesPage({
  page,
  competitor,
  recommendations,
  relatedCompetitors,
}: AlternativesPageProps) {
  const canonicalUrl = `${siteUrl}/alternatives/${page.slug}/`;
  const trialHref = signupHref(page.slug);
  const ctaLabel = page.ctaLabel ?? "Start with free application credits";

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Best AI auto-apply tools",
        item: `${siteUrl}/best-ai-auto-apply-tools/`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: `${competitor.name} alternatives`,
        item: canonicalUrl,
      },
    ],
  };

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: page.title,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: recommendations.length,
    itemListElement: recommendations.map((product, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: product.name,
      url: product.officialUrl,
    })),
  };

  return (
    <div className={styles.page}>
      <Head>
        <title>{page.title}</title>
        <meta name="description" content={page.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${siteUrl}/widget.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.metaDescription} />
        <meta name="twitter:image" content={`${siteUrl}/widget.png`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbJsonLd),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
        />
      </Head>

      <Navbar />

      <main>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.eyebrow}>
              Published by LifeShack · {competitor.name} sources reviewed {competitor.verifiedAt}
            </p>
            <h1>{page.title}</h1>
            <p className={styles.subheadline}>{page.directAnswer}</p>
            <div className={styles.heroActions}>
              <SeoCtaLink
                href={trialHref}
                pageType="alternative"
                placement="hero"
                competitorSlug={page.slug}
                className={styles.primaryCta}
              >
                {ctaLabel} <ArrowRight size={18} />
              </SeoCtaLink>
              <a className={styles.secondaryCta} href="#alternatives">
                Compare all seven
              </a>
            </div>
            <div className={styles.trialProof} aria-label="Introductory offer details">
              <span>
                <CheckCircle2 size={16} /> {FREE_TRIAL_APPLICATION_CREDITS} application credits included
              </span>
              <span>
                <CheckCircle2 size={16} /> No credit card required
              </span>
              <span>
                <ShieldCheck size={16} /> Official product sources cited
              </span>
            </div>
          </div>
        </section>

        <section className={styles.sectionAlt}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Why consider a different tool?</p>
              <h2>
                {competitor.name} is useful for {competitor.bestFit.toLowerCase()}
              </h2>
              <p>
                Its meaningful limitation is that {competitor.limitation.toLowerCase()}
                {" "}
                A different workflow may fit better when:
              </p>
            </div>

            <div className={styles.summaryGrid}>
              <article className={styles.summaryPanel}>
                <div className={styles.summaryIcon}>
                  <Search size={22} />
                </div>
                <h3>How {competitor.name} works</h3>
                <p>{competitor.productModel}.</p>
                <div className={styles.bestForBox}>
                  <span>Candidate control</span>
                  <strong>{competitor.userControl}</strong>
                </div>
                <h4>Current access</h4>
                <p>{competitor.freeAccess}</p>
              </article>

              <article className={styles.summaryPanel}>
                <div className={`${styles.summaryIcon} ${styles.summaryIconAlt}`}>
                  <Target size={22} />
                </div>
                <h3>Reasons to switch</h3>
                <ul>
                  {page.reasonsToSwitch.map((reason) => (
                    <li key={reason}>
                      <CheckCircle2 size={17} /> {reason}
                    </li>
                  ))}
                </ul>
                <div className={styles.bestForBox}>
                  <span>Pricing note</span>
                  <strong>{competitor.pricingSummary}</strong>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className={styles.section} id="alternatives">
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Seven practical alternatives</p>
              <h2>
                Compare the workflow before replacing {competitor.name}.
              </h2>
              <p>
                These products are ordered by fit for someone actively
                evaluating a {competitor.name} replacement. “Best for” describes
                the use case—not a user rating.
              </p>
            </div>

            <div className={styles.comparisonTable} role="table">
              <div className={styles.tableHeader} role="row">
                <div role="columnheader">Alternative</div>
                <div role="columnheader">Best for</div>
                <div role="columnheader">Workflow and control</div>
              </div>
              {recommendations.map((product, index) => (
                <div className={styles.tableRow} role="row" key={product.slug}>
                  <div
                    className={`${styles.tableCell} ${styles.featureName}`}
                    role="cell"
                  >
                    <strong>
                      {index + 1}. {product.name}
                    </strong>
                    <span className={styles.mobileColumnLabel}>
                      {workflowLabels[product.workflow]}
                    </span>
                  </div>
                  <div className={styles.tableCell} role="cell">
                    <span className={styles.mobileColumnLabel}>Best for</span>
                    {product.bestFit}
                  </div>
                  <div
                    className={`${styles.tableCell} ${
                      product.slug === "lifeshack" ? styles.lifeshackCell : ""
                    }`}
                    role="cell"
                  >
                    <span className={styles.mobileColumnLabel}>
                      Workflow and control
                    </span>
                    <strong>{workflowLabels[product.workflow]}.</strong>{" "}
                    {product.userControl}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.verdictPanel}>
              <div>
                <p className={styles.eyebrow}>Our recommendation</p>
                <h3>
                  {page.verdictHeading ??
                    "Choose LifeShack when application execution—not another dashboard—is the bottleneck."}
                </h3>
                <p>
                  {page.verdictBody ??
                    "LifeShack created this page and ranks its own product first for that specific use case. Use the product details, source links, and free application credits to verify the fit for yourself."}
                </p>
              </div>
              <SeoCtaLink
                href={trialHref}
                pageType="alternative"
                placement="comparison_table"
                competitorSlug={page.slug}
                className={styles.smallCta}
              >
                {ctaLabel} <ArrowRight size={17} />
              </SeoCtaLink>
            </div>
          </div>
        </section>

        <section className={styles.sectionAlt}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Detailed recommendations</p>
              <h2>What each {competitor.name} alternative is best for.</h2>
            </div>
            <div className={styles.hubGrid}>
              {recommendations.map((product, index) => (
                <article className={styles.hubCard} key={product.slug}>
                  <div className={styles.hubCardTop}>
                    <span>#{index + 1} · {workflowLabels[product.workflow]}</span>
                    {product.slug === "lifeshack" ? (
                      <div className={styles.hubBadge}>Our product</div>
                    ) : null}
                  </div>
                  <h3>{product.name}</h3>
                  <p>
                    <strong>Best for:</strong> {product.bestFit}
                  </p>
                  <p>
                    <strong>Limitation:</strong> {product.limitation}
                  </p>
                  {product.slug === "lifeshack" ? (
                    <p>
                      See{" "}
                      <Link href="/ai-auto-apply/">
                        <a className={styles.inlineLink}>how LifeShack Auto Apply works</a>
                      </Link>
                      , including setup and application controls.
                    </p>
                  ) : null}
                  <p>
                    <strong>Pricing and free access:</strong>{" "}
                    {product.pricingSummary} {product.freeAccess}
                  </p>
                  {product.slug !== "lifeshack" ? (
                    <p><strong>Sources reviewed:</strong> {product.verifiedAt}</p>
                  ) : null}
                  {product.slug === "lifeshack" ? (
                    <SeoCtaLink
                      href={trialHref}
                      pageType="alternative"
                      placement={`recommendation_${index + 1}`}
                      competitorSlug={page.slug}
                      className={styles.hubLink}
                    >
                      {ctaLabel} <ChevronRight size={16} />
                    </SeoCtaLink>
                  ) : (
                    <a
                      className={styles.hubLink}
                      href={product.officialUrl}
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                    >
                      Verify on official site <ExternalLink size={15} />
                    </a>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Methodology and disclosure</p>
              <h2>How this list was built.</h2>
              <p>
                LifeShack publishes this comparison and includes its own product.
                This is official-source research, not a hands-on product test.
                Sources for {competitor.name} were reviewed on {competitor.verifiedAt};
                each alternative’s source-review date appears on its card.
                We compared product model, browser
                versus cloud workflow, job discovery, autofill, submission,
                tailoring, tracking, free access, user control, and best-fit
                use case. We did not use fabricated ratings, user-review scores,
                or paid placement. Pricing and features can change, so verify
                current terms before purchasing.
              </p>
            </div>

            <div className={styles.summaryGrid}>
              <article className={styles.summaryPanel}>
                <div className={styles.summaryIcon}>
                  <ShieldCheck size={22} />
                </div>
                <h3>Source for {competitor.name}</h3>
                <p>
                  The official product pages below support the feature and
                  pricing summaries used in this comparison.
                </p>
                <ul>
                  {competitor.sources.map((source) => (
                    <li key={source.href}>
                      <ExternalLink size={17} />
                      <a
                        href={source.href}
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                      >
                        {source.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </article>
              <article className={styles.summaryPanel}>
                <div className={`${styles.summaryIcon} ${styles.summaryIconAlt}`}>
                  <Sparkles size={22} />
                </div>
                <h3>Continue your research</h3>
                <ul>
                  <li>
                    <CheckCircle2 size={17} />
                    <Link href={`/compare/${page.slug}/`}>
                      <a>Read LifeShack vs {competitor.name}</a>
                    </Link>
                  </li>
                  <li>
                    <CheckCircle2 size={17} />
                    <Link href="/best-ai-auto-apply-tools/">
                      <a>Compare the best AI auto-apply tool categories</a>
                    </Link>
                  </li>
                  {relatedCompetitors.map((related) => (
                    <li key={related.slug}>
                      <CheckCircle2 size={17} />
                      <Link href={`/alternatives/${related.slug}/`}>
                        <a>Explore {related.name} alternatives</a>
                      </Link>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className={styles.ctaBand}>
          <div className={styles.ctaInner}>
            <div>
              <p className={styles.eyebrow}>Test the recommendation</p>
              <h2>
                {page.ctaHeading ?? "See whether LifeShack fits your job search."}
              </h2>
              <p>
                {page.ctaDescription ??
                  "Start with free application credits—no credit card required—and see how well LifeShack works for your job search."}
              </p>
            </div>
            <SeoCtaLink
              href={trialHref}
              pageType="alternative"
              placement="bottom_cta"
              competitorSlug={page.slug}
              className={styles.primaryCta}
            >
              {ctaLabel} <ArrowRight size={18} />
            </SeoCtaLink>
          </div>
        </section>

        <SeoCtaLink
          href={trialHref}
          pageType="alternative"
          placement="mobile_sticky"
          competitorSlug={page.slug}
          className={styles.mobileTrial}
        >
          {ctaLabel} <ArrowRight size={17} />
        </SeoCtaLink>
      </main>

      <Footer />
    </div>
  );
}

AlternativesPage.isPublicSeoPage = true;

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: alternativePages.map((page) => ({
    params: { brand: page.slug },
  })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<AlternativesPageProps> = async ({
  params,
}) => {
  const slug = typeof params?.brand === "string" ? params.brand : "";
  const page = getAlternativePage(slug);
  const competitor = getCompetitorProfile(slug);

  if (!page || !competitor) {
    return { notFound: true };
  }

  const recommendations = getAlternativeRecommendations(page);
  const relatedCompetitors = recommendations
    .filter(
      (product): product is CompetitorProfile =>
        product.slug !== "lifeshack" && Boolean(getCompetitorProfile(product.slug))
    )
    .map((product) => getCompetitorProfile(product.slug) as CompetitorProfile)
    .slice(0, 3);

  return {
    props: {
      page,
      competitor,
      recommendations,
      relatedCompetitors,
    },
  };
};
